import { useState } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { InlineToggleButton } from '~/components/ui/buttons/InlineToggleButton';
import { AddressFields } from '~/components/common/AddressFields';
import { MapPickerDialog } from './MapPickerDialog';
import { Input } from '~/components/ui/inputs/Input';
import type { AddressFormState } from '~/utils/address';

export type EditorMode = 'address' | 'coordinates';

export type EditorAnnouncement = {
  kind: 'point_updated' | 'resolve_failed';
  seq: number;
} | null;

type Props = {
  mode: EditorMode;
  form: AddressFormState;
  latitude: string;
  longitude: string;
  coordinatesValid: boolean;
  pendingLookup: boolean;
  resolveFailed: boolean;
  announcement: EditorAnnouncement;
  mapLatitude: number | undefined;
  mapLongitude: number | undefined;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  saveDisabled: boolean;
  cancelDisabled: boolean;
  onPick: (latitude: number, longitude: number) => void;
  onModeChange: (mode: EditorMode) => void;
  onFormChange: (form: AddressFormState) => void;
  onLatitudeChange: (value: string) => void;
  onLongitudeChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
};

export function DeliveryAddressEditor({
  mode,
  form,
  latitude,
  longitude,
  coordinatesValid,
  pendingLookup,
  resolveFailed,
  announcement,
  mapLatitude,
  mapLongitude,
  centerLatitude,
  centerLongitude,
  saveDisabled,
  cancelDisabled,
  onPick,
  onModeChange,
  onFormChange,
  onLatitudeChange,
  onLongitudeChange,
  onCancel,
  onSave,
}: Readonly<Props>) {
  const { t } = useTranslation('cart');
  const [isMapOpen, setIsMapOpen] = useState(false);

  return (
    <div className="space-y-3">
      {isMapOpen && (
        <MapPickerDialog
          isOpen={isMapOpen}
          initialLatitude={mapLatitude}
          initialLongitude={mapLongitude}
          centerLatitude={centerLatitude}
          centerLongitude={centerLongitude}
          onConfirm={(latitude, longitude) => {
            setIsMapOpen(false);
            onPick(latitude, longitude);
          }}
          onClose={() => setIsMapOpen(false)}
        />
      )}
      {pendingLookup && (
        <p
          data-testid="reverse-geocode-pending"
          className="text-xs text-neut-sec"
        >
          {t('cart.delivery_address.resolving')}
        </p>
      )}
      {resolveFailed && (
        <p
          data-testid="reverse-geocode-failed"
          className="text-xs text-amber-700"
        >
          {t('cart.delivery_address.resolve_failed')}
        </p>
      )}
      <div aria-live="polite" className="sr-only">
        {announcement && (
          <p key={announcement.seq} data-testid="delivery-address-announcement">
            {announcement.kind === 'point_updated'
              ? t('cart.delivery_address.point_updated')
              : t('cart.delivery_address.resolve_failed')}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-3">
          <InlineToggleButton
            testId="delivery-address-mode-address"
            label={t('cart.delivery_address.enter_address')}
            active={mode === 'address'}
            onClick={() => onModeChange('address')}
          />
          <InlineToggleButton
            testId="delivery-address-mode-coordinates"
            label={t('cart.delivery_address.enter_coordinates')}
            active={mode === 'coordinates'}
            onClick={() => onModeChange('coordinates')}
          />
        </div>
        <button
          type="button"
          data-testid="delivery-address-pick-on-map"
          className="inline-flex items-center gap-1.5 rounded-md border border-neut-200 px-2.5 py-1.5 text-xs font-medium text-neut-700 hover:bg-neut-50"
          onClick={() => setIsMapOpen(true)}
        >
          <MapIcon className="w-3.5 h-3.5 text-prim-500" />
          {t('cart.delivery_address.pick_on_map')}
        </button>
      </div>
      {mode === 'address' ? (
        <AddressFields
          value={form}
          onChange={onFormChange}
          requireFullAddress={false}
        />
      ) : (
        <>
          <Input
            label={t('cart.delivery_address.latitude')}
            name="latitude"
            type="number"
            step="any"
            min={-90}
            max={90}
            value={latitude}
            onChange={(_name, value) => onLatitudeChange(value)}
            placeholder="49.2827"
          />
          <Input
            label={t('cart.delivery_address.longitude')}
            name="longitude"
            type="number"
            step="any"
            min={-180}
            max={180}
            value={longitude}
            onChange={(_name, value) => onLongitudeChange(value)}
            placeholder="-123.1207"
          />
          {!coordinatesValid && (latitude !== '' || longitude !== '') && (
            <p
              data-testid="coordinates-invalid"
              className="text-xs text-red-600"
            >
              {t('cart.delivery_address.coordinates_invalid')}
            </p>
          )}
        </>
      )}
      <div className="flex gap-2">
        <Button
          variant="secondary"
          disabled={cancelDisabled}
          onClick={onCancel}
        >
          {t('cart.delivery_address.cancel')}
        </Button>
        <Button
          data-testid="delivery-address-save"
          disabled={saveDisabled}
          onClick={onSave}
        >
          {t('cart.delivery_address.save')}
        </Button>
      </div>
      {mode === 'address' && (
        <p className="text-xs text-neut-sec italic">
          {t('cart.delivery_address.hint')}
        </p>
      )}
    </div>
  );
}
